import React, { useEffect, useState } from "react"
import dayjs from "dayjs"

import EntitlementsForm from "../EntitlementsForm/EntitlementsForm"
import { LoadingSpinner } from "terra-one"
import {
  TrialFormStart,
  ProductSummary,
  LayoutGeneral,
  LayoutFAQs,
} from "trimble-terra"

import "./TrialWorkFlow.scss"
import {
  formatPageLinks,
  updateDownloadLink,
} from "../../utils/organism-formatting/formatPageLinks"
import getConfigTID from "../../utils/functions/getConfigTID"
import { refreshAuthToken } from "../../utils/functions/refreshAuthToken"

import error from "../../images/error.webp"
import {
  getTrialFormId,
  checkForHealth,
  startTrial,
  getTrialDetails,
  submitToWorkato
} from "../../utils/functions/getTrialForm"
import {
  createProductDetailPageLink,
  createProductTrialPageLink,
} from "../../components/CTAButtons/CTAButtonsConnector"

const TrialWorkFlow = ({
  data,
  appData,
  locale,
  innerPageNavData,
  setInnerPageNavData,
  region,
  regionList,
  siteIdentifier,
  dataStorageEnv,
  dataStorageSpaceId,
  dataStorageAccessToken,
  product,
}) => {
  const [auth, setAuth] = useState(false)
  const [checkHealthOfTrialsAPI, setCheckHealthOfTrialsAPI] = useState(false)
  const [loading, setLoading] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const [activeTrial, setActiveTrial] = useState(null)
  const [endedTrial, setEndedTrial] = useState(false)
  const [error, setError] = useState(false)
  const [errorText, setErrorText] = useState(true)
  const [daysLeftOfTrial, setDaysLeftOfTrial] = useState(null)
  useEffect(() => {
    if (!product?.sku) {
      setError(true)
      setErrorText("The product you are looking for does not exist.")
    }
  }, [product])

  const userLoggedIn =
    typeof window !== "undefined"
      ? localStorage?.getItem("user_info")
        ? true
        : false
      : false

  const {
    queryString,
    redirect_uri,
    code_verifier,
    client_id,
    url,
    client_secret,
    signUpQuery,
  } = getConfigTID(siteIdentifier)

  // useEffect has all fetch calls
  useEffect(() => {
    if (userLoggedIn && !userInfo && typeof window !== "undefined") {
      setUserInfo(JSON.parse(localStorage.getItem("user_info")))
    }
    const getHealthCheck = () => {
      // checkForHealth is first API call
      // This checks to see if middleware API works
      checkForHealth(
        setLoading,
        setError,
        setCheckHealthOfTrialsAPI,
        setErrorText,
        setAuth
      )
    }

    // this checks if user has valid credentials
    // access token needed for all fetch calls is only active the first 10 minutes after authenticating
    // if user authenticated more than 10 minutes prior to hitting this page, new access token needs to be generated
    const checkIfUserHasValidAuthToken = () => {
      if (typeof window !== "undefined") {
        // check if user is logged in
        if (userLoggedIn) {
          const timeStamp = JSON.parse(
            localStorage.getItem("user-trial-data")
          )?.time
          // checks if access token is still valid
          if (
            timeStamp &&
            getTrialFormId(Number(timeStamp)) &&
            !activeTrial &&
            JSON.parse(localStorage.getItem("user-trial-data"))?.id
          ) {
            // checks if there is an active trial for user
            getTrialDetails(product?.sku, setActiveTrial, setLoading, setError)
            setAuth(true)
          } else {
            refreshAuthToken(
              client_id,
              client_secret,
              redirect_uri,
              code_verifier,
              window?.location?.pathname,
              url,
              null,
              null,
              null,
              null
            )
          }
        } else {
          setAuth(false)
          setLoading(false)
        }
      }
    }
    if (!activeTrial && typeof window !== "undefined") {
      if (userLoggedIn) {
        getHealthCheck()
      }
      checkIfUserHasValidAuthToken()
    }
  }, [
    checkHealthOfTrialsAPI,
    auth,
    activeTrial,
    product?.sku,
    userInfo,
    userLoggedIn,
  ])

  useEffect(() => {
    const getLicensingData = () => {
      if (activeTrial && activeTrial?.length) {
        const getLicense = activeTrial?.find(trial => {
          return trial?.sku?.id === product?.sku
        })
        if (getLicense?.status && getLicense?.status === "expired") {
          setDaysLeftOfTrial(0)
          setEndedTrial(true)
        } else if (getLicense?.status === "active") {
          const trialEndDate = dayjs(getLicense?.deactivationDate)
          setDaysLeftOfTrial(trialEndDate.diff(Date.now(), "day") + 1)
        }
      }
    }
    if (typeof window !== "undefined") {
      getLicensingData()
    }
  }, [activeTrial, product?.sku])

  //formats data for components
  const formatProductSummaryCard = () => {
    if (!daysLeftOfTrial || daysLeftOfTrial === 0 || daysLeftOfTrial < 0) {
      return {
        name: data?.productName,
        description: data?.trialSummaryCardCustomText,
        productLine: `0 ${data?.trialLengthLabel || "Days "}`,
      }
    }
    return {
      name: data?.productName,
      description: data?.longDescription?.longDescription,
      productLine:
        daysLeftOfTrial && daysLeftOfTrial < 2
          ? `${daysLeftOfTrial} ${data?.trialLengthSingular || "Day"}`
          : `${daysLeftOfTrial} ${data?.trialLengthLabel || "Days "}`,
      ctas: [
        {
          text: data?.downloadLabelWindows,
          url: product?.LaunchLink_Web,
          className: "primary--1",
        },
        {
          text: data?.viewYourProductsLabel,
          url: process.env.GATSBY_PRODUCTS_LINK
            ? process.env.GATSBY_PRODUCTS_LINK
            : "https://accounts.trimble.com/products",
          className: "primary--2",
        },
      ],
    }
  }

  //formats data for components
  const productSummary = {
    variant:
      !daysLeftOfTrial || daysLeftOfTrial === 0 || daysLeftOfTrial < 0
        ? "product-trial-last-day"
        : "product-trial",
    summaryCard: formatProductSummaryCard(),
    labels: {
      productSummaryLabel: data?.trialSummaryLabel,
      productLineLabel: data?.daysRemainingLabel,
    },
    columns: [
      {
        eyebrow: data?.inTrialProductSummary?.columnOneEyebrow,
        header: data?.inTrialProductSummary?.columnOneHeader,
        subheader: data?.inTrialProductSummary?.columnOneDescription,
        listType:
          data?.inTrialProductSummary?.columnOneBullets?.[0]?.__typename ===
          "ContentfulPoint"
            ? "bullets"
            : data?.inTrialProductSummary?.columnOneBullets?.[0]?.__typename ===
              "ContentfulLink"
            ? "links"
            : null,
        list: formatList(
          data?.inTrialProductSummary?.columnOneBullets,
          "en",
          null
        ),
      },
      {
        eyebrow: data?.inTrialProductSummary?.columnTwoEyebrow,
        header: data?.inTrialProductSummary?.columnTwoHeader,
        subheader: data?.inTrialProductSummary?.columnTwoDescription,
        listType:
          data?.inTrialProductSummary?.columnTwoBullets?.[0]?.__typename ===
          "ContentfulPoint"
            ? "bullets"
            : data?.inTrialProductSummary?.columnTwoBullets?.[0]?.__typename ===
              "ContentfulLink"
            ? "links"
            : null,
        list: formatList(
          data?.inTrialProductSummary?.columnTwoBullets,
          "en",
          null
        ),
      },
    ],
  }
  //formats data for components
  const trialSpecifics = {
    header:
      data?.trialFormStartLabel && data?.productName
        ? `${data?.trialFormStartLabel} ${data?.productName}`
        : `Start a Trial of ${data?.productName}`,
    text:
      product?.shortDescription ||
      "Connect the right people to the right data at the right time - free for 30 days!",
    // text: "Create professional work with our most robust tools - free for 30 days!",
    signInText: data?.signInTextLabel,
    ctas: [
      {
        text: data?.signInLabel || "Sign In",
        onClick:
          typeof window !== "undefined"
            ? // TODO - update to pull from getTIDConfig instead of hard coding staging
              () =>
                window.open(
                  `${queryString}/${locale}/products/${data?.productName
                    .toLowerCase()
                    .replaceAll(" ", "-")}/trial`,
                  "_self"
                )
            : () => {},

        className: "primary--1",
      },
      {
        text: data?.createAnAccountLabel || "Create An Account",
        onClick: () =>
          window.open(
            `${signUpQuery}/${locale}/products/${data?.productName
              .toLowerCase()
              .replaceAll(" ", "-")}/trial`,
            "_self"
          ),
        className: "primary--2",
      },
    ],
  }

  //formats data for components
  const trialSummary = {
    eyebrow: data?.trialSummaryLabel || "Trial Summary",
    productName: data?.productName,
    productNameIcon: data?.productIcon?.file?.url
      ? {
          src: data?.productIcon?.file?.url,
          alt: data?.productIcon?.file?.fileName,
        }
      : null,
    // body: product?.shortDescription,
    body: data?.trialSummaryCustomText,
    bullets: data?.customContentListForTrialSummary?.length
      ? data?.customContentListForTrialSummary
      : [],
    durationLabel: data?.durationLabel || "Duration",
    duration:
      product?.Trial_Duration && product?.Trial_Duration < 10
        ? `${product?.Trial_Duration} ${data?.trialLengthSingular || "Day"}`
        : product?.Trial_Duration
        ? `${product?.Trial_Duration} ${data?.trialLengthLabel || "Days "}`
        : null,
    operatingSystemLabel: data?.operatingSystemLabel || "Operating System",
    operatingSystem: data?.windowsLabel || "Windows",
  }

  //formats data for components
  const content = {
    trialSpecifics,
    trialSummary,
  }

  //formats data for components
  const buttons = data?.errorLinks?.length
    ? data?.errorLinks?.map(link => {
        return {
          primary: true,
          label: formatPageLinks(link, appData?.locale)?.text,
          destination: formatPageLinks(link, appData?.locale)?.url,
        }
      })
    : []
  const heroErrorContent = {
    orientation: "left",
    isHeaderBig: false,
    isWrapped: true,
    presetTheme: "t2-preset-theme-two",
    header: data?.anErrorOccurredLabel,
    subHeader: JSON.stringify(errorText?.statusCode),
    description: errorText?.errorMessage,
    buttons,
    image: {
      src: data?.errorImage?.image?.file?.url
        ? data?.errorImage?.image?.file?.url
        : error,
      alt: data?.errorImage?.image?.title
        ? data?.errorImage?.image?.title
        : "error",
    },
  }

  const formatTextWithCTA = content => {
    if (content) {
      return {
        presetTheme: "t2-preset-theme-two",
        eyebrow: content?.eyebrow,
        header: content?.headline,
        description: content?.subhead,
        buttons: content?.ctaButtons?.map(btn => {
          return {
            primary: true,
            label: btn?.text,
            url: url?.url,
          }
        }),
      }
    }
  }

  //formats data for components
  const renderQuestionsAndAnswers = () => {
    const accordionData =
      data?.ctaWithTextAndHeaderWithAccordionForTrials?.find(component => {
        return component?.__typename === "ContentfulAccordion"
      })

    const ctaWithHeader =
      data?.ctaWithTextAndHeaderWithAccordionForTrials?.find(component => {
        return component?.__typename === "ContentfulCtaWithTextAndHeader"
      })

    const formatAccordionAndCTASection = (cta, accordion) => {
      let ctaSection
      let accordionSection
      if (cta) {
        ctaSection = {
          eyebrow: cta?.eyebrow,
          header: cta?.headline,
          description: cta?.subhead,
        }
      }
      if (accordion) {
        accordionSection = accordion?.items?.map(item => {
          return {
            headline: item?.headline,
            description: item?.body?.childMarkdownRemark?.html,
          }
        })
      }

      return { ...ctaSection, faqItems: accordionSection }
    }
    return (
      <div className="trial-form__question-container">
        <LayoutFAQs
          {...formatAccordionAndCTASection(ctaWithHeader, accordionData)}
        />
      </div>
    )
  }
  return (
    <section className="trial-form">
      <div className="trial-form--grid-wrapper">
        {loading && (
          <section className="trial-form__loading-spinner">
            <LoadingSpinner />
          </section>
        )}

        {!activeTrial && !loading && !error && !endedTrial && (
          <div className="trial-form__content-container">
            <section className="trial-form__content-container--forms">
              {/* Before Login */}
              {!auth && !loading && <TrialFormStart content={content} />}
              {/* After Logged before trial starts */}
              {auth && checkHealthOfTrialsAPI && !activeTrial && !loading && (
                <EntitlementsForm
                  trialSummary={trialSummary}
                  marketoForm={data?.marketoFormForTrialSubmission}
                  appData={appData}
                  userInfo={userInfo}
                  productData={product}
                  startTrial={startTrial}
                  setLoading={setLoading}
                  setError={setError}
                  setErrorText={setErrorText}
                  loading={loading}
                  submitToWorkato={submitToWorkato}
                />
              )}
            </section>
          </div>
        )}
        {auth &&
          activeTrial &&
          checkHealthOfTrialsAPI &&
          !endedTrial &&
          !loading &&
          !error && (
            <div className="trial-form__started-container">
              {/* Your trial has started */}
              {data?.inTrialMessagingHeaderTextAndCta && (
                <LayoutFAQs
                  {...formatTextWithCTA(data?.inTrialMessagingHeaderTextAndCta)}
                />
              )}
              {productSummary && <ProductSummary content={productSummary} />}
            </div>
          )}
        {auth && endedTrial && !loading && !error && (
          <div className="trial-form__ended-container">
            {/* Your trial has ended */}
            {data?.yourTrialHasEndedHeaderTextAndCta && (
              <LayoutFAQs
                {...formatTextWithCTA(data?.yourTrialHasEndedHeaderTextAndCta)}
              />
            )}
            {productSummary && <ProductSummary content={productSummary} />}
          </div>
        )}
        {/* Error state */}
        {error && !loading && (
          <div className="trial-form__error-container">
            {heroErrorContent && <LayoutGeneral {...heroErrorContent} />}
          </div>
        )}
      </div>

      {renderQuestionsAndAnswers()}
    </section>
  )
}

//formats data for components
const formatList = (list, locale, currentSpaceId) => {
  if (list && list.length) {
    if (list?.[0]?.__typename === "ContentfulPoint") {
      return list.map(item => {
        return { title: item?.header, text: item?.description }
      })
    }

    if (list?.[0]?.__typename === "ContentfulLink") {
      return list.map(item => {
        let itemUrl = item?.url?.url
        if (item?.url?.__typename === "ContentfulProductDetailPage") {
          itemUrl = createProductDetailPageLink(locale, item?.url)?.url
        }
        if (item?.url?.__typename === "ContentfulProductTrialPage") {
          itemUrl = createProductTrialPageLink(locale, item?.url)?.url
        }
        if (item?.url?.realLink?.url) {
          itemUrl = updateDownloadLink(item?.url?.realLink?.url, currentSpaceId)
        }
        if (item?.url?.internal?.type === "ContentfulPageBase") {
          itemUrl = `/${locale}/${item?.url?.url}`
        }
        if (item?.url?.internal?.type === "ContentfulProductPage") {
          itemUrl = `/${locale}/products/${item?.url?.url}`
        }
        return {
          url: itemUrl,
          text:
            item?.url?.__typename === "ContentfulProductDetailPage" ||
            item?.url?.__typename === "ContentfulProductTrialPage"
              ? item?.text
              : item?.url?.text,
          type:
            item?.url?.type && item?.url?.type === "ContentfulDownloadLink"
              ? "pdf"
              : null,
        }
      })
    }
  }
}

export default TrialWorkFlow
